import React, { useEffect, useState } from 'react'
import ReactPaginate from 'react-paginate'
import { Helmet } from 'react-helmet'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
  TextField,
  Typography,
  FormControl,
  CircularProgress,
} from '@mui/material'
import { CreditCard, Drafts, UndoRounded, Delete } from '@mui/icons-material'
import ResponsiveAppBar from '../common/ResponsiveAppBar'
import {
  searchInvoicePaymentChecks,
  SearchReceivedInvoicePaymentChecksTotals,
} from 'src/services/SearchService'
import { InvoicePaymentCheck } from 'src/viewModels/InvoicePaymentCheck'
import UnApplyCheckModal from './UnApplyCheckModal'
import MarkAsDeletedModal from './MarkAsDeletedModal'
import { Tooltip } from '@mui/material'
import { ArchiveOutline } from 'mdi-material-ui'
import CheckHistoryModal from './CheckHistoryModal'
import {
  GetReceivedCheckUnappliedHistoryDetail,
  GetReceivedCheckAppliedFundsDetail,
} from 'src/services/AdminService'
import { InvoiceCheckDetailSummary } from 'src/viewModels/InvoiceCheckDetailSummary'
import moment from 'moment'

const ReceivedChecks: React.FC = () => {
  const [gridData, setGridData] = useState<InvoicePaymentCheck[]>([])
  const [gridLoading, setGridLoading] = useState(false)
  const [totalItems, setTotalItems] = useState(0)
  const [firstRecordIndex, setFirstRecordIndex] = useState(0)
  const [lastRecordIndex, setLastRecordIndex] = useState(0)
  const [pageCount, setPageCount] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(25)

  const [checkNumber, setCheckNumber] = useState('')
  const [payer, setPayer] = useState('')
  const [checkDateStart, setCheckDateStart] = useState<string>('')
  const [checkDateEnd, setCheckDateEnd] = useState<string>('')
  const [hasSearched, setHasSearched] = useState(false)

  const [unapplyDialogOpen, setUnapplyDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedCheck, setSelectedCheck] = useState<InvoicePaymentCheck | null>(null)
  const [checkCount, setCheckCount] = useState(0)
  const [totalCheckAmount, setTotalCheckAmount] = useState(0)
  const [totalCheckAppliedFunds, setTotalCheckAppliedFunds] = useState(0)
  const [, setDeletedChecks] = useState<Set<string>>(new Set())
  const [countsLoading, setCountsLoading] = useState(false)
  const [isUnappliedClicked, setIsUnappliedClicked] = useState(false)

  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [detailCheck, setDetailCheck] = useState<InvoiceCheckDetailSummary | null>(null)

  const handleOpenDetailModal = async (
    check: InvoicePaymentCheck,
    unapplied: boolean
  ) => {
    let detailData: InvoiceCheckDetailSummary | null = null
    if (unapplied) {
      const result = await GetReceivedCheckUnappliedHistoryDetail(check.id.toString())
      detailData = typeof result === 'string' ? null : result
    } else {
      const result = await GetReceivedCheckAppliedFundsDetail(check.id.toString())
      detailData = typeof result === 'string' ? null : result
    }
    setDetailCheck(detailData)
    setDetailModalOpen(true)
    setIsUnappliedClicked(unapplied)
    setSelectedCheck(check)
  }
  const handleCloseDetailModal = () => {
    setDetailModalOpen(false)
    setDetailCheck(null)
  }

  const setPaginationOffsetData = (total: number) => {
    const first = (page - 1) * pageSize + 1
    const last = page * pageSize < total ? page * pageSize : total
    setFirstRecordIndex(first)
    setLastRecordIndex(last)
  }
  const CurrencyFormat: any = require('react-currency-format')

  const isSearchGridDataEmpty = () => !gridData || gridData.length === 0

  const paginationInfo = () => {
    if (!isSearchGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {firstRecordIndex} to {lastRecordIndex} of {totalItems} entries
          </Typography>
        </div>
      )
    }
    return <></>
  }

  const handleOpenUnapplyDialog = (check: InvoicePaymentCheck) => {
    setSelectedCheck(check)
    setUnapplyDialogOpen(true)
  }

  const handleOpenDeleteDialog = (check: InvoicePaymentCheck) => {
    setSelectedCheck(check)
    setDeleteDialogOpen(true)
  }

  const handleCloseUnapplyDialog = (reload: boolean) => {
    setUnapplyDialogOpen(false)
    setSelectedCheck(null)
    if (reload && selectedCheck) {
      loadCheckGrid()
    }
  }

  const handleCloseDeleteDialog = (reload: boolean) => {
    setDeleteDialogOpen(false)
    setSelectedCheck(null)
    if (reload && selectedCheck) {
      setDeletedChecks((prev) => new Set(prev).add(selectedCheck.checkNumber))
      loadCheckGrid()
    }
  }

  const mapSearchGridData = () =>
    isSearchGridDataEmpty() ? (
      <TableRow>
        <TableCell colSpan={8} align="center">
          No records found
        </TableCell>
      </TableRow>
    ) : (
      gridData.map((item, index) => {
        const isUnapplied = !item.hasAppliedFunds
        const isDeleted = item.markedDeleted === true
        return (
          <TableRow className="gridPadding" key={index}>
            <TableCell style={{ width: '5%' }}>
              {item.hasHistoricalUnappliedFunds ? (
                <>
                  <Tooltip title="Click to view unapplied funds history">
                    <span>
                      <ArchiveOutline
                        style={{
                          cursor: 'pointer',
                        }}
                        onClick={() => handleOpenDetailModal(item, true)}
                      />
                    </span>
                  </Tooltip>
                  &nbsp;&nbsp;
                </>
              ) : (
                <>
                  <ArchiveOutline style={{ visibility: 'hidden' }} />
                  &nbsp;&nbsp;
                </>
              )}
              {item.hasAppliedFunds ? (
                <>
                  <Tooltip title="Click to view applied funds detail">
                    <CreditCard
                      style={{
                        cursor: 'pointer',
                      }}
                      onClick={() => handleOpenDetailModal(item, false)}
                    />
                  </Tooltip>
                  &nbsp;&nbsp;
                </>
              ) : (
                <>
                  <CreditCard style={{ visibility: 'hidden' }} />
                  &nbsp;&nbsp;
                </>
              )}
            </TableCell>
            <TableCell
              style={
                isDeleted
                  ? { textDecoration: 'line-through', textDecorationColor: 'red' }
                  : undefined
              }
            >
              {item.checkNumber}
            </TableCell>
            <TableCell
              style={
                isDeleted
                  ? { textDecoration: 'line-through', textDecorationColor: 'red' }
                  : undefined
              }
            >
              {item.payer}
            </TableCell>
            <TableCell
              style={
                isDeleted
                  ? {
                      textAlign: 'right',
                      textDecoration: 'line-through',
                      textDecorationColor: 'red',
                    }
                  : { textAlign: 'right' }
              }
            >
              {item.checkDate ? moment.utc(item.checkDate).format('MM/DD/YYYY') : ''}
            </TableCell>
            <TableCell
              style={
                isDeleted
                  ? {
                      textAlign: 'right',
                      textDecoration: 'line-through',
                      textDecorationColor: 'red',
                    }
                  : { textAlign: 'right' }
              }
            >
              {item.amount !== undefined ? `$${item.amount.toLocaleString()}` : ''}
            </TableCell>
            <TableCell
              style={
                isDeleted
                  ? {
                      textAlign: 'right',
                      textDecoration: 'line-through',
                      textDecorationColor: 'red',
                    }
                  : { textAlign: 'right' }
              }
            >
              {item.amountApplied !== undefined
                ? `$${item.amountApplied.toLocaleString()}`
                : ''}
            </TableCell>
            <TableCell
              style={
                isDeleted
                  ? {
                      textAlign: 'right',
                      textDecoration: 'line-through',
                      textDecorationColor: 'red',
                    }
                  : { textAlign: 'right' }
              }
            >
              {item.remainingFunds !== undefined
                ? `$${item.remainingFunds.toLocaleString()}`
                : ''}
            </TableCell>
            <TableCell
              style={
                isDeleted
                  ? {
                      textAlign: 'right',
                      textDecoration: 'line-through',
                      textDecorationColor: 'red',
                    }
                  : { textAlign: 'right' }
              }
            >
              {item.numberOfInvoices}
            </TableCell>
            <TableCell>
              {!isDeleted ? (
                isUnapplied ? (
                  <Tooltip title="Delete">
                    <Button
                      color="secondary"
                      onClick={() => handleOpenDeleteDialog(item)}
                      style={{ borderColor: 'red', color: 'red', float: 'right' }}
                    >
                      <Delete />
                    </Button>
                  </Tooltip>
                ) : (
                  <Tooltip title="Unapply">
                    <Button
                      color="secondary"
                      disabled={item.amountApplied === 0}
                      onClick={() => handleOpenUnapplyDialog(item)}
                      style={{ borderColor: 'red', color: 'red', float: 'right' }}
                    >
                      <UndoRounded />
                    </Button>
                  </Tooltip>
                )
              ) : null}
            </TableCell>
          </TableRow>
        )
      })
    )

  const clearSearch = async () => {
    setCheckNumber('')
    setPayer('')
    setCheckDateStart('')
    setCheckDateEnd('')
    setPage(1)
    setTotalItems(0)
    setPageCount(0)
    setGridData([])
    setGridLoading(false)
  }

  const search = async (event: React.FormEvent) => {
    event.preventDefault()
    setGridLoading(true)
    setCountsLoading(true)
    setPage(1)
    setHasSearched(true)
    setCheckCount(0)
    setTotalCheckAmount(0)
    setTotalCheckAppliedFunds(0)
    try {
      const searchBody = {
        checkNumber,
        checkDateStart: checkDateStart ? new Date(checkDateStart) : undefined,
        checkDateEnd: checkDateEnd ? new Date(checkDateEnd) : undefined,
        payer,
        page: 1,
        pageSize,
      }
      const response = await searchInvoicePaymentChecks(
        searchBody.checkNumber,
        searchBody.payer,
        searchBody.checkDateStart,
        searchBody.checkDateEnd,
        searchBody.page,
        searchBody.pageSize
      )
      const { items, totalItems, totalPages } = response
      setTotalItems(totalItems)
      setPageCount(totalPages)
      setGridData(items)
      if (totalItems > 0) setPaginationOffsetData(totalItems)
      SearchReceivedInvoicePaymentChecksTotals(
        searchBody.checkNumber,
        searchBody.payer,
        searchBody.checkDateStart,
        searchBody.checkDateEnd
      ).then((totals) => {
        setCheckCount(totals.checkCount)
        setTotalCheckAmount(totals.totalCheckAmount)
        setTotalCheckAppliedFunds(totals.totalCheckAppliedFunds)
        setCountsLoading(false)
      })
    } finally {
      setGridLoading(false)
    }
  }

  const loadCheckGrid = async () => {
    setGridLoading(true)
    const response = await searchInvoicePaymentChecks(
      checkNumber,
      payer,
      checkDateStart ? new Date(checkDateStart) : undefined,
      checkDateEnd ? new Date(checkDateEnd) : undefined,
      page,
      pageSize
    )
    const { items, totalItems, totalPages } = response
    setTotalItems(totalItems)
    setPageCount(totalPages)
    setGridData(items)
    if (totalItems > 0) setPaginationOffsetData(totalItems)
    setGridLoading(false)
  }

  const handlePageChange = async (event: any) => {
    const newPage = event.selected + 1
    setPage(newPage)
  }

  useEffect(() => {
    if (hasSearched) {
      loadCheckGrid()
    }
  }, [page, pageSize, hasSearched])

  return (
    <>
      <Helmet>
        <title>Received Checks</title>
      </Helmet>
      <ResponsiveAppBar title="Received Checks" pageIcon={<Drafts />} />
      <Paper style={{ margin: 20, height: '100%', paddingBottom: 20 }}>
        {(gridLoading || countsLoading) && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <form onSubmit={search}>
          <Grid container style={{ padding: 10, width: '100%' }}>
            <Typography
              color="primary"
              variant="h6"
              gutterBottom
              style={{ paddingBottom: 10 }}
            >
              SEARCH CRITERIA
            </Typography>
            <Grid container direction="row" spacing={2} style={{ width: '100%' }}>
              <Grid item xs={3}>
                <FormControl fullWidth>
                  <TextField
                    value={checkNumber}
                    onChange={(e) => setCheckNumber(e.target.value)}
                    label="Check Number"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={3}>
                <FormControl fullWidth>
                  <TextField
                    value={payer}
                    onChange={(e) => setPayer(e.target.value)}
                    label="Payer"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={2}>
                <FormControl fullWidth>
                  <TextField
                    value={checkDateStart}
                    onChange={(e) => setCheckDateStart(e.target.value)}
                    label="Check Start Date"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    type="date"
                    InputProps={{
                      inputProps: { max: new Date().toISOString().split('T')[0] },
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={2}>
                <FormControl fullWidth>
                  <TextField
                    value={checkDateEnd}
                    onChange={(e) => setCheckDateEnd(e.target.value)}
                    label="Check End Date"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    type="date"
                    InputProps={{
                      inputProps: { max: new Date().toISOString().split('T')[0] },
                    }}
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              spacing={2}
              style={{
                marginLeft: 1,
                marginTop: 10,
                paddingTop: 5,
                paddingBottom: 10,
                width: '100%',
              }}
            >
              <Button
                type="submit"
                color="primary"
                size="medium"
                variant="contained"
                style={{ marginRight: 10 }}
              >
                Search
              </Button>
              <Button
                color="secondary"
                onClick={clearSearch}
                size="medium"
                variant="contained"
              >
                Clear Search
              </Button>
            </Grid>
            <Grid item container direction="row" style={{ paddingTop: '20px' }}>
              <Grid item xs={12}>
                <Typography noWrap variant="h5">
                  Check Count:&nbsp;
                  <strong>{isSearchGridDataEmpty() ? 'N/A' : checkCount}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography noWrap variant="h5">
                  Total Funds:&nbsp;
                  <strong>
                    {isSearchGridDataEmpty() ? (
                      'N/A'
                    ) : (
                      <CurrencyFormat
                        value={totalCheckAmount}
                        displayType={'text'}
                        thousandSeparator={true}
                        prefix={'$'}
                        fixedDecimalScale={true}
                        decimalScale={2}
                      />
                    )}
                  </strong>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography noWrap variant="h5">
                  Total Applied Funds:&nbsp;
                  <strong>
                    {isSearchGridDataEmpty() ? (
                      'N/A'
                    ) : (
                      <CurrencyFormat
                        value={totalCheckAppliedFunds}
                        displayType={'text'}
                        thousandSeparator={true}
                        prefix={'$'}
                        fixedDecimalScale={true}
                        decimalScale={2}
                      />
                    )}
                  </strong>
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </form>
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignContent="center"
          alignItems="flex-start"
          wrap="wrap"
        >
          <Grid item xs={12}>
            <Table aria-label="simple table" style={{ padding: 20 }}>
              <TableHead>
                <TableRow className="gridPadding">
                  <TableCell style={{ width: '10%' }}></TableCell>
                  <TableCell style={{ width: '13%' }}>Check Number</TableCell>
                  <TableCell style={{ width: '13%' }}>Payer</TableCell>
                  <TableCell style={{ width: '13%', textAlign: 'right' }}>
                    Check Date
                  </TableCell>
                  <TableCell style={{ width: '13%', textAlign: 'right' }}>
                    Check Amount
                  </TableCell>
                  <TableCell style={{ width: '13%', textAlign: 'right' }}>
                    Amount Applied
                  </TableCell>
                  <TableCell style={{ width: '13%', textAlign: 'right' }}>
                    Remaining Funds
                  </TableCell>
                  <TableCell style={{ width: '13%', textAlign: 'right' }}>
                    # of Invoices
                  </TableCell>
                  <TableCell style={{ width: '9%', textAlign: 'right' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{mapSearchGridData()}</TableBody>
            </Table>
            <div className="pagination-row">
              {paginationInfo()}
              <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                onPageChange={handlePageChange}
                forcePage={page - 1}
                pageCount={pageCount}
                containerClassName={'pagination'}
                activeClassName={'active'}
                renderOnZeroPageCount={() => null}
              />
            </div>
          </Grid>
        </Grid>
        {selectedCheck && (
          <>
            <UnApplyCheckModal
              open={unapplyDialogOpen}
              close={handleCloseUnapplyDialog}
              check={selectedCheck}
            />
            <MarkAsDeletedModal
              open={deleteDialogOpen}
              close={handleCloseDeleteDialog}
              check={selectedCheck}
            />
          </>
        )}
      </Paper>
      <CheckHistoryModal
        open={detailModalOpen}
        onClose={handleCloseDetailModal}
        check={detailCheck}
        isUnapplied={isUnappliedClicked}
      />
    </>
  )
}

export default ReceivedChecks
